/**
 * Advertisements Management - Platform admin page for managing ads
 * Premium dark mode design
 */

import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Megaphone,
  Plus,
  Search,
  Eye,
  MousePointer,
  DollarSign,
  Calendar,
  Edit2,
  CheckCircle,
  XCircle,
  Clock,
  Image,
  ExternalLink,
  BarChart3,
  Filter,
  Upload,
  Video,
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Status badge styles
const statusStyles = {
  draft: 'bg-slate-400/20 text-slate-400 border-slate-400/30',
  pending_review: 'bg-soft-amber/20 text-soft-amber border-soft-amber/30',
  active: 'bg-cool-mint/20 text-cool-mint border-cool-mint/30',
  paused: 'bg-slate-400/20 text-slate-400 border-slate-400/30',
  completed: 'bg-electric-ice-blue/20 text-electric-ice-blue border-electric-ice-blue/30',
  rejected: 'bg-signal-red/20 text-signal-red border-signal-red/30',
};

// Placement labels - will be replaced by dynamic placements
const defaultPlacementLabels = {
  event_banner: 'Event Banner',
  discover_feed: 'Discover Feed',
  discover_top: 'Discovery Top Banner',
  discover_featured: 'Featured Spot',
  discover_bottom: 'Discovery Bottom',
  pass_footer: 'Pass Footer',
  pass_header: 'Pass Header',
  scanner_idle: 'Scanner Idle Screen',
  scanner_sidebar: 'Scanner Sidebar',
  leaderboard_sidebar: 'Leaderboard Sidebar',
  leaderboard_banner: 'Leaderboard Banner',
  passport_banner: 'Passport Banner',
  app_interstitial: 'Full-Screen Interstitial',
};

// Glass panel component
const GlassPanel = ({ children, className = '', ...props }) => (
  <div
    className={`bg-white/[0.03] backdrop-blur-xl border border-white/[0.06] rounded-2xl ${className}`}
    {...props}
  >
    {children}
  </div>
);

// Ad card
const AdCard = ({ ad, onEdit, onApprove, onReject }) => {
  const statusStyle = statusStyles[ad.status] || statusStyles.draft;

  return (
    <GlassPanel className="p-5 hover:border-white/[0.12] transition-all duration-300">
      <div className="flex gap-4">
        {/* Preview */}
        <div className="w-24 h-24 rounded-xl bg-white/[0.05] flex items-center justify-center flex-shrink-0 overflow-hidden">
          {ad.image_url ? (
            <img src={ad.image_url} alt={ad.title} className="w-full h-full object-cover" />
          ) : (
            <Image className="w-8 h-8 text-muted-text" />
          )}
        </div>

        {/* Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <div>
              <h3 className="font-medium text-cloud-white">{ad.title}</h3>
              <p className="text-sm text-muted-text">{ad.advertiser_name}</p>
            </div>
            <Badge className={`${statusStyle} border`}>
              {ad.status?.replace('_', ' ').toUpperCase()}
            </Badge>
          </div>

          <div className="flex flex-wrap items-center gap-3 mt-3 text-xs">
            <span className="flex items-center gap-1 text-soft-silver bg-white/[0.05] px-2 py-1 rounded">
              {defaultPlacementLabels[ad.placement] || ad.placement}
            </span>
            {ad.start_date && (
              <span className="flex items-center gap-1 text-muted-text">
                <Calendar className="w-3 h-3" />
                {new Date(ad.start_date).toLocaleDateString()}
              </span>
            )}
          </div>

          {/* Stats */}
          <div className="flex items-center gap-4 mt-3 pt-3 border-t border-white/[0.06]">
            <div className="flex items-center gap-1 text-xs">
              <Eye className="w-3 h-3 text-electric-ice-blue" />
              <span className="text-soft-silver">{ad.total_impressions || 0}</span>
            </div>
            <div className="flex items-center gap-1 text-xs">
              <MousePointer className="w-3 h-3 text-cool-mint" />
              <span className="text-soft-silver">{ad.total_clicks || 0}</span>
            </div>
            <div className="flex items-center gap-1 text-xs">
              <DollarSign className="w-3 h-3 text-soft-amber" />
              <span className="text-soft-silver">${(ad.total_spend || 0).toFixed(2)}</span>
            </div>
            {ad.click_url && (
              <a 
                href={ad.click_url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1 text-xs text-electric-ice-blue hover:underline ml-auto"
              >
                <ExternalLink className="w-3 h-3" /> Preview
              </a>
            )}
          </div>

          {/* Actions */}
          <div className="flex items-center gap-2 mt-3">
            <Button variant="outline" size="sm" className="h-8" onClick={() => onEdit(ad)}>
              <Edit2 className="w-3 h-3 mr-1" /> Edit
            </Button>
            {ad.status === 'pending_review' && (
              <>
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="h-8 border-cool-mint/30 text-cool-mint hover:bg-cool-mint/10"
                  onClick={() => onApprove(ad)}
                >
                  <CheckCircle className="w-3 h-3 mr-1" /> Approve
                </Button>
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="h-8 border-signal-red/30 text-signal-red hover:bg-signal-red/10"
                  onClick={() => onReject(ad)}
                >
                  <XCircle className="w-3 h-3 mr-1" /> Reject
                </Button>
              </>
            )}
          </div>
        </div>
      </div>
    </GlassPanel>
  );
};

// Create/Edit ad form
const AdForm = ({ ad, onSave, onClose }) => {
  const [form, setForm] = useState({
    advertiser_name: ad?.advertiser_name || '',
    title: ad?.title || '',
    description: ad?.description || '',
    image_url: ad?.image_url || '',
    video_url: ad?.video_url || '',
    click_url: ad?.click_url || '',
    cta_text: ad?.cta_text || 'Learn More',
    placement: ad?.placement || 'discover_feed',
    start_date: ad?.start_date?.split('T')[0] || '',
    end_date: ad?.end_date?.split('T')[0] || '',
    daily_budget: ad?.daily_budget || 0,
    total_budget: ad?.total_budget || 0,
    cost_per_impression: ad?.cost_per_impression || 0,
    cost_per_click: ad?.cost_per_click || 0,
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    await onSave(form);
    setSaving(false);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6 py-4">
      <div className="space-y-4">
        <div>
          <Label htmlFor="advertiser_name">Advertiser Name *</Label>
          <Input
            id="advertiser_name"
            value={form.advertiser_name}
            onChange={(e) => setForm({ ...form, advertiser_name: e.target.value })}
            required
            className="mt-1.5 bg-white/[0.03] border-white/[0.1]"
          />
        </div>

        <div>
          <Label htmlFor="title">Ad Title *</Label>
          <Input
            id="title"
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            required
            className="mt-1.5 bg-white/[0.03] border-white/[0.1]"
          />
        </div>

        <div>
          <Label htmlFor="description">Description</Label>
          <Textarea
            id="description"
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            className="mt-1.5 bg-white/[0.03] border-white/[0.1]"
            rows={2}
          />
        </div>

        <div>
          <Label htmlFor="placement">Placement</Label>
          <Select value={form.placement} onValueChange={(v) => setForm({ ...form, placement: v })}>
            <SelectTrigger className="mt-1.5 bg-white/[0.03] border-white/[0.1]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="max-h-[300px]">
              {Object.entries(defaultPlacementLabels).map(([value, label]) => (
                <SelectItem key={value} value={value}>{label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label htmlFor="image_url">Image</Label>
            <div className="mt-1.5 space-y-2">
              <Input
                id="image_url"
                value={form.image_url}
                onChange={(e) => setForm({ ...form, image_url: e.target.value })}
                className="bg-white/[0.03] border-white/[0.1]"
                placeholder="https://... or upload below"
              />
              <div className="flex items-center gap-2">
                <Label htmlFor="image_upload" className="flex-1 cursor-pointer">
                  <div className="flex items-center justify-center gap-2 px-3 py-2 bg-white/[0.03] border border-dashed border-white/[0.1] rounded-lg hover:border-electric-ice-blue/50 transition-colors">
                    <Upload className="w-4 h-4 text-muted-text" />
                    <span className="text-sm text-muted-text">Upload Image</span>
                  </div>
                  <input
                    id="image_upload"
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      if (file.size > 10 * 1024 * 1024) {
                        toast.error('File must be less than 10MB');
                        return;
                      }
                      const reader = new FileReader();
                      reader.onload = () => {
                        setForm({ ...form, image_url: reader.result });
                        toast.success('Image uploaded - will be stored when you save');
                      };
                      reader.readAsDataURL(file);
                    }}
                  />
                </Label>
                {form.image_url && (
                  <div className="w-12 h-12 rounded bg-white/[0.05] overflow-hidden flex-shrink-0">
                    <img src={form.image_url} alt="Preview" className="w-full h-full object-cover" />
                  </div>
                )}
              </div>
            </div>
          </div>
          <div>
            <Label htmlFor="click_url">Click URL</Label>
            <Input
              id="click_url"
              value={form.click_url}
              onChange={(e) => setForm({ ...form, click_url: e.target.value })}
              className="mt-1.5 bg-white/[0.03] border-white/[0.1]"
              placeholder="https://..."
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label htmlFor="cta_text">CTA Text</Label>
            <Input
              id="cta_text"
              value={form.cta_text}
              onChange={(e) => setForm({ ...form, cta_text: e.target.value })}
              className="mt-1.5 bg-white/[0.03] border-white/[0.1]"
            />
          </div>
          <div>
            <Label htmlFor="video_url">Video</Label>
            <div className="mt-1.5 space-y-2">
              <Input
                id="video_url"
                value={form.video_url}
                onChange={(e) => setForm({ ...form, video_url: e.target.value })}
                className="bg-white/[0.03] border-white/[0.1]"
                placeholder="https://... or upload below"
              />
              <Label htmlFor="video_upload" className="cursor-pointer block">
                <div className="flex items-center justify-center gap-2 px-3 py-2 bg-white/[0.03] border border-dashed border-white/[0.1] rounded-lg hover:border-electric-ice-blue/50 transition-colors">
                  <Video className="w-4 h-4 text-muted-text" />
                  <span className="text-sm text-muted-text">Upload Video (max 50MB)</span>
                </div>
                <input
                  id="video_upload"
                  type="file"
                  accept="video/*"
                  className="hidden"
                  onChange={async (e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    if (file.size > 50 * 1024 * 1024) {
                      toast.error('Video must be less than 50MB');
                      return;
                    }
                    const reader = new FileReader();
                    reader.onload = () => {
                      setForm({ ...form, video_url: reader.result });
                      toast.success('Video uploaded - will be stored when you save');
                    };
                    reader.readAsDataURL(file);
                  }}
                />
              </Label>
            </div>
          </div>
        </div>

        <div className="pt-4 border-t border-white/[0.06]">
          <h4 className="text-sm font-medium text-cloud-white mb-3">Schedule</h4>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="start_date">Start Date</Label>
              <Input
                id="start_date"
                type="date"
                value={form.start_date}
                onChange={(e) => setForm({ ...form, start_date: e.target.value })}
                className="mt-1.5 bg-white/[0.03] border-white/[0.1]"
              />
            </div>
            <div>
              <Label htmlFor="end_date">End Date</Label>
              <Input
                id="end_date"
                type="date"
                value={form.end_date}
                onChange={(e) => setForm({ ...form, end_date: e.target.value })}
                className="mt-1.5 bg-white/[0.03] border-white/[0.1]"
              />
            </div>
          </div>
        </div>

        <div className="pt-4 border-t border-white/[0.06]">
          <h4 className="text-sm font-medium text-cloud-white mb-3">Budget & Pricing</h4>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="daily_budget">Daily Budget ($)</Label>
              <Input
                id="daily_budget"
                type="number"
                step="0.01"
                value={form.daily_budget}
                onChange={(e) => setForm({ ...form, daily_budget: parseFloat(e.target.value) || 0 })}
                className="mt-1.5 bg-white/[0.03] border-white/[0.1]"
              />
            </div>
            <div>
              <Label htmlFor="total_budget">Total Budget ($)</Label>
              <Input
                id="total_budget"
                type="number"
                step="0.01"
                value={form.total_budget}
                onChange={(e) => setForm({ ...form, total_budget: parseFloat(e.target.value) || 0 })}
                className="mt-1.5 bg-white/[0.03] border-white/[0.1]"
              />
            </div>
            <div>
              <Label htmlFor="cost_per_impression">CPM ($)</Label>
              <Input
                id="cost_per_impression"
                type="number"
                step="0.0001"
                value={form.cost_per_impression}
                onChange={(e) => setForm({ ...form, cost_per_impression: parseFloat(e.target.value) || 0 })}
                className="mt-1.5 bg-white/[0.03] border-white/[0.1]"
              />
            </div>
            <div>
              <Label htmlFor="cost_per_click">CPC ($)</Label>
              <Input
                id="cost_per_click"
                type="number"
                step="0.0001"
                value={form.cost_per_click}
                onChange={(e) => setForm({ ...form, cost_per_click: parseFloat(e.target.value) || 0 })}
                className="mt-1.5 bg-white/[0.03] border-white/[0.1]"
              />
            </div>
          </div>
        </div>
      </div>

      <div className="flex justify-end gap-3 pt-4 border-t border-white/[0.06]">
        <Button type="button" variant="outline" onClick={onClose}>
          Cancel
        </Button>
        <Button type="submit" disabled={saving}>
          {saving ? 'Saving...' : ad ? 'Update Ad' : 'Create Ad'}
        </Button>
      </div>
    </form>
  );
};

export const Advertisements = () => {
  const { session, profile } = useAuth();
  const [ads, setAds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingAd, setEditingAd] = useState(null);

  // Check if user is platform admin
  const isPlatformAdmin = ['platform_owner', 'platform_admin'].includes(profile?.platform_role);

  // Fetch ads
  const fetchAds = useCallback(async () => {
    if (!session?.access_token) {
      setLoading(false);
      return;
    }

    try {
      let url = `${API_URL}/api/ads?limit=100`;
      if (statusFilter && statusFilter !== 'all') url += `&status=${statusFilter}`;

      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${session.access_token}` },
      });

      if (res.ok) {
        const data = await res.json();
        setAds(data.advertisements || []);
      } else {
        console.warn('Ads API returned', res.status);
      }
    } catch (err) {
      console.error('Failed to fetch ads:', err);
    } finally {
      setLoading(false);
    }
  }, [session?.access_token, statusFilter]);

  useEffect(() => {
    fetchAds();
  }, [fetchAds]);

  // Create/Update ad
  const handleSaveAd = async (formData) => {
    try {
      const url = editingAd
        ? `${API_URL}/api/ads/${editingAd.id}`
        : `${API_URL}/api/ads`;
      
      const res = await fetch(url, {
        method: editingAd ? 'PUT' : 'POST',
        headers: {
          Authorization: `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (res.ok) {
        toast.success(editingAd ? 'Ad updated' : 'Ad created');
        fetchAds();
        setIsFormOpen(false);
        setEditingAd(null);
      } else {
        toast.error('Failed to save ad');
      }
    } catch (err) {
      console.error('Failed to save ad:', err);
      toast.error('Failed to save ad');
    }
  };

  // Approve ad
  const handleApproveAd = async (ad) => {
    try {
      const res = await fetch(`${API_URL}/api/ads/${ad.id}/approve`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${session.access_token}` },
      });

      if (res.ok) {
        toast.success('Ad approved');
        fetchAds();
      }
    } catch (err) {
      console.error('Failed to approve ad:', err);
    }
  };

  // Reject ad
  const handleRejectAd = async (ad) => {
    const reason = prompt('Enter rejection reason:');
    if (!reason) return;

    try {
      const res = await fetch(`${API_URL}/api/ads/${ad.id}/reject?reason=${encodeURIComponent(reason)}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${session.access_token}` },
      });

      if (res.ok) {
        toast.success('Ad rejected');
        fetchAds();
      }
    } catch (err) {
      console.error('Failed to reject ad:', err);
    }
  };

  // Filter ads
  const filteredAds = ads.filter((a) =>
    a.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    a.advertiser_name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Stats
  const totalImpressions = ads.reduce((sum, a) => sum + (a.total_impressions || 0), 0);
  const totalClicks = ads.reduce((sum, a) => sum + (a.total_clicks || 0), 0);
  const totalSpend = ads.reduce((sum, a) => sum + (a.total_spend || 0), 0);
  const activeAds = ads.filter((a) => a.status === 'active').length;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="flex flex-col items-center gap-4">
          <div className="w-8 h-8 border-2 border-electric-ice-blue border-t-transparent rounded-full animate-spin" />
          <p className="text-soft-silver text-sm">Loading advertisements...</p>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="advertisements-page" className="space-y-6 pb-8">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-semibold text-cloud-white flex items-center gap-3">
            <Megaphone className="w-8 h-8 text-electric-ice-blue" />
            Advertisements
          </h1>
          <p className="text-soft-silver mt-1">Manage ad campaigns and placements</p>
        </div>

        {isPlatformAdmin && (
          <Button onClick={() => { setEditingAd(null); setIsFormOpen(true); }}>
            <Plus className="w-4 h-4 mr-2" /> Create Ad
          </Button>
        )}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <GlassPanel className="p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-muted-text text-sm">Active Ads</p>
              <p className="text-2xl font-mono font-bold text-cool-mint mt-1">{activeAds}</p>
            </div>
            <CheckCircle className="w-6 h-6 text-cool-mint" />
          </div>
        </GlassPanel>
        <GlassPanel className="p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-muted-text text-sm">Impressions</p>
              <p className="text-2xl font-mono font-bold text-electric-ice-blue mt-1">
                {totalImpressions.toLocaleString()}
              </p>
            </div>
            <Eye className="w-6 h-6 text-electric-ice-blue" />
          </div>
        </GlassPanel>
        <GlassPanel className="p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-muted-text text-sm">Clicks</p>
              <p className="text-2xl font-mono font-bold text-soft-amber mt-1">
                {totalClicks.toLocaleString()}
              </p>
            </div>
            <MousePointer className="w-6 h-6 text-soft-amber" />
          </div>
        </GlassPanel>
        <GlassPanel className="p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-muted-text text-sm">Total Spend</p>
              <p className="text-2xl font-mono font-bold text-cloud-white mt-1">
                ${totalSpend.toFixed(2)}
              </p>
            </div>
            <DollarSign className="w-6 h-6 text-cool-mint" />
          </div>
        </GlassPanel>
      </div>

      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-text" />
          <Input
            placeholder="Search ads..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 bg-white/[0.03] border-white/[0.1]"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[180px] bg-white/[0.03] border-white/[0.1]">
            <Filter className="w-4 h-4 mr-2" />
            <SelectValue placeholder="All Statuses" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            <SelectItem value="draft">Draft</SelectItem>
            <SelectItem value="pending_review">Pending Review</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="paused">Paused</SelectItem>
            <SelectItem value="completed">Completed</SelectItem>
            <SelectItem value="rejected">Rejected</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Ads List */}
      {filteredAds.length > 0 ? (
        <div className="space-y-4">
          {filteredAds.map((ad) => (
            <AdCard
              key={ad.id}
              ad={ad}
              onEdit={(a) => { setEditingAd(a); setIsFormOpen(true); }}
              onApprove={handleApproveAd}
              onReject={handleRejectAd}
            />
          ))}
        </div>
      ) : (
        <GlassPanel className="p-12 text-center">
          <Megaphone className="w-12 h-12 text-muted-text mx-auto mb-4" />
          <h3 className="text-lg font-medium text-cloud-white mb-2">No Advertisements</h3>
          <p className="text-muted-text mb-4">
            Create your first ad campaign to get started.
          </p>
          {isPlatformAdmin && (
            <Button onClick={() => setIsFormOpen(true)}>
              <Plus className="w-4 h-4 mr-2" /> Create Ad
            </Button>
          )}
        </GlassPanel>
      )}

      {/* Form Sheet */}
      <Sheet open={isFormOpen} onOpenChange={setIsFormOpen}>
        <SheetContent className="bg-graphite border-white/[0.06] w-full sm:max-w-xl overflow-y-auto">
          <SheetHeader>
            <SheetTitle className="text-cloud-white">
              {editingAd ? 'Edit Advertisement' : 'Create Advertisement'}
            </SheetTitle>
            <SheetDescription className="text-muted-text">
              {editingAd ? 'Update advertisement details' : 'Create a new ad campaign'}
            </SheetDescription>
          </SheetHeader>
          <AdForm
            ad={editingAd}
            onSave={handleSaveAd}
            onClose={() => { setIsFormOpen(false); setEditingAd(null); }}
          />
        </SheetContent>
      </Sheet>
    </div>
  );
};

export default Advertisements;
